/**
 * The stand-alone chat Admin-management home: the member list, the
 * per-member overflow menu (`Remove from chat`, `Make Admin`, `Block` and
 * `Report` all real; `Block` goes through the shared `useBlockFan`
 * orchestration and `Report` opens `ReportAccountDialog`, ISSUE-283's closing
 * pass), `Rename
 * chat`, `Change photo`, `Invite people` (a real exact username search against
 * `/chat/invite-members`, ISSUE-283 building on ISSUE-760's
 * `/profile/search-by-username`), `Delete chat`, and `Leave chat` with the
 * sole-Admin transfer prompt.
 *
 * EVERY ACTION THIS MENU CAN DRAW IS WIRED, so it carries no not-ready state.
 * It used to: ISSUE-785 built a `READY_CHAT_MEMBER_ACTION_IDS` set that gated
 * both `handleOverflowSelect` and the per-button `disabled`,
 * `aria-describedby` and reason-paragraph rendering. That was right for its
 * moment, because `Block` and `Report` were not wired yet. ISSUE-283's closing
 * pass then made both real and added them to the set, which left it holding
 * all four ids of `typeChatMemberMenuActionId`. From that commit on, the guard
 * could never return early and no button could ever render disabled.
 *
 * ISSUE-818 deleted it rather than reshaping it, because the set of not-ready
 * actions here is EMPTY and there is no control to disable. The runtime check
 * is replaced by a STRONGER compile-time one: the `switch` below now has an
 * exhaustive `default` that assigns `id` to `never`, so a fifth menu id is a
 * build error naming the one place that must handle it. Before, the `switch`
 * had no `default` at all, so a fifth id would have compiled and silently done
 * nothing. The mobile twin of this menu,
 * `apps/mobile/app/(tabs)/messages/member-actions.tsx`, shed the same dead set
 * for the same reason.
 *
 * `MEMBER_ACTIONS_NOT_READY_NOTE` is no longer imported here, and is NOT gone:
 * it stays defined and live in `../poolMembers/MemberActionsDialog`, which
 * keeps the mechanism although, since ISSUE-1045, its only caller marks every
 * id ready.
 */
import {
  useChatDeleteStandalone,
  useChatGet,
  useChatInviteMembers,
  useChatLeave,
  useChatListMembers,
  useChatMemberTimeoutClear,
  useChatPromoteAdmin,
  useChatRemoveMember,
  useChatUpdate,
} from "@sportsmet/messaging-sdk/private";
import { useProfileSearchByUsername } from "@sportsmet/profile-sdk/private";
import {
  Button,
  confirmDestructive,
  Dialog,
  InlineBanner,
  Input,
  Skeleton,
} from "@sportsmet/ui";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";

import { useAuth } from "../../auth/AuthProvider";
import {
  extractErrorCode,
  showApiError,
  showOfflineError,
} from "../../errors/apiErrorBus";
import {
  normalizeUsername,
  USERNAME_PATTERN,
} from "../editProfile/editProfileLogic";
import {
  PHOTO_UPLOAD_FAILED_MESSAGE,
  PHOTO_UPLOADING_LABEL,
} from "../editProfile/editProfileLogic";
import { openFanProfileLabel } from "../fanProfile/fanProfileCopy";
import { ReportAccountDialog } from "../messageModeration/ReportAccountDialog";
import { useBlockFan } from "../messageModeration/useBlockFan";
import { resolveInviteRowState } from "./chatInvitePeopleLogic";
import {
  buildChatMemberOverflowMenu,
  isChatAdminRole,
  isOnlyMember,
  isSoleAdmin,
  type typeChatMemberMenuActionId,
  type typeChatMemberRole,
} from "./chatMemberOverflowMenu";
import { ChatPhotoPickerButton } from "./ChatPhotoPickerButton";
import { MemberTimeoutDialog } from "./MemberTimeoutDialog";
import { type typeChatMemberCandidate } from "./newChatAddPeople";
import {
  buildMemberRemoveConfirmTitle,
  CHAT_CHANGE_PHOTO_LABEL,
  CHAT_DELETE_CONFIRM_BODY,
  CHAT_DELETE_CONFIRM_TITLE,
  CHAT_DELETE_LABEL,
  CHAT_INVITE_ALREADY_MEMBER_LABEL,
  CHAT_INVITE_PEOPLE_LABEL,
  CHAT_INVITE_SEARCH_ACTION_LABEL,
  CHAT_INVITE_SEARCH_SENT_LABEL,
  CHAT_LEAVE_CONFIRM_BODY,
  CHAT_LEAVE_CONFIRM_TITLE,
  CHAT_LEAVE_LABEL,
  CHAT_MEMBERS_TITLE,
  CHAT_RENAME_LABEL,
  MEMBER_REMOVE_CONFIRM_BODY,
  MEMBER_REMOVE_LABEL,
  NEW_CHAT_ADD_PEOPLE_NOT_FOUND_MESSAGE,
  NEW_CHAT_ADD_PEOPLE_SEARCH_LABEL,
  NEW_CHAT_ADD_PEOPLE_SEARCHING_LABEL,
  NEW_CHAT_ADD_PEOPLE_START_MESSAGE,
  NEW_CHAT_NAME_LABEL,
  ONLY_MEMBER_LEAVE_BODY,
  ONLY_MEMBER_LEAVE_TITLE,
  SOLE_ADMIN_TRANSFER_BODY,
  SOLE_ADMIN_TRANSFER_TITLE,
} from "./standaloneChatCopy";
import { useChatPhotoUpload } from "./useChatPhotoUpload";

type typeInvitePhase = "idle" | "searching" | "found" | "notFound";

type typeOverflowTarget = {
  userId: string;
  displayName: string;
  role: typeChatMemberRole;
  /** ISSUE-1364. Shown to officers only, by /chat/list-members. */
  timedOutUntilIso: string | null;
};

export function ChatMembersDialog({
  open,
  onOpenChange,
  chatId,
  onLeftOrDeleted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chatId: string;
  onLeftOrDeleted: () => void;
}): ReactNode {
  const { state: authState } = useAuth();
  const viewerUserId =
    authState.status === "signedIn" ? authState.user.userId : "";
  const queryClient = useQueryClient();

  const isEnabled = open && chatId !== "";
  const chatGet = useChatGet({ chatId }, { enabled: isEnabled });
  const chatMembers = useChatListMembers({ chatId }, { enabled: isEnabled });

  const removeMember = useChatRemoveMember();
  const promoteAdmin = useChatPromoteAdmin();
  const updateChat = useChatUpdate();
  const leaveChat = useChatLeave();
  const deleteChat = useChatDeleteStandalone();
  const inviteMembers = useChatInviteMembers();
  const { state: photoState, uploadPhoto } = useChatPhotoUpload();
  const { blockFan } = useBlockFan();

  const [overflowTarget, setOverflowTarget] =
    useState<typeOverflowTarget | null>(null);
  const [reportTarget, setReportTarget] = useState<typeOverflowTarget | null>(
    null,
  );
  // ISSUE-1364. Who the Time out dialog is about.
  const [timeoutTarget, setTimeoutTarget] = useState<typeOverflowTarget | null>(
    null,
  );
  const timeoutClear = useChatMemberTimeoutClear();
  const [isInviting, setIsInviting] = useState(false);
  const [inviteUsernameQuery, setInviteUsernameQuery] = useState("");
  const [sentInviteUserIds, setSentInviteUserIds] = useState<Set<string>>(
    new Set(),
  );
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [showTransferPrompt, setShowTransferPrompt] = useState(false);

  useEffect(() => {
    const code = extractErrorCode(chatGet.data);
    if (code !== null)
      showApiError(chatGet.data, undefined, chatGet.dataUpdatedAt);
  }, [chatGet.dataUpdatedAt, chatId]);

  useEffect(() => {
    const code = extractErrorCode(chatMembers.data);
    if (code !== null)
      showApiError(chatMembers.data, undefined, chatMembers.dataUpdatedAt);
  }, [chatMembers.dataUpdatedAt, chatId]);

  // The search is exact-match only server side (Q-760-A.1), the same rule
  // `NewChatDialog`'s create-time search follows: a fan must type the whole
  // handle before anything fires.
  const normalizedInviteQuery = normalizeUsername(inviteUsernameQuery);
  const isInviteQueryComplete = USERNAME_PATTERN.test(normalizedInviteQuery);

  const inviteSearch = useProfileSearchByUsername(
    { username: normalizedInviteQuery },
    { enabled: isEnabled && isInviting && isInviteQueryComplete },
  );

  useEffect(() => {
    if (!isInviting || !isInviteQueryComplete) return;
    if (inviteSearch.isError) {
      showOfflineError();
      return;
    }
    const code = extractErrorCode(inviteSearch.data);
    if (code !== null) {
      showApiError(inviteSearch.data, undefined, inviteSearch.dataUpdatedAt);
    }
  }, [
    isInviting,
    isInviteQueryComplete,
    inviteSearch.isError,
    inviteSearch.dataUpdatedAt,
    normalizedInviteQuery,
  ]);

  useEffect(() => {
    if (photoState.kind !== "ready") return;
    void updateChat
      .mutateAsync({
        chatId,
        photoMediaId: photoState.mediaId,
      })
      .then((result) => {
        if (result.error) {
          showApiError(result);
          return;
        }
        void queryClient.invalidateQueries();
      });
    // Runs once per upload reaching "ready".
  }, [photoState]);

  const members = chatMembers.data?.data?.items ?? [];
  const isViewerOnlyMember = isOnlyMember({ viewerUserId, members });
  const myRole: typeChatMemberRole = chatGet.data?.data?.myRole ?? "member";
  const chatTitle = chatGet.data?.data?.title ?? "";
  const isViewerAdmin = isChatAdminRole(myRole);
  const existingMemberUserIds = new Set(members.map((member) => member.userId));

  let invitePhase: typeInvitePhase;
  if (!isInviteQueryComplete) {
    invitePhase = "idle";
  } else if (
    inviteSearch.isError ||
    extractErrorCode(inviteSearch.data) !== null
  ) {
    // A miss, a non-discoverable fan, and a blocked pair all answer with the
    // identical empty result server side (Q-760-A.4), and a peer outage
    // fails the query itself, matching `NewChatDialog`'s own phase table.
    invitePhase = "idle";
  } else if (inviteSearch.isLoading) {
    invitePhase = "searching";
  } else if ((inviteSearch.data?.data?.items.length ?? 0) > 0) {
    invitePhase = "found";
  } else {
    invitePhase = "notFound";
  }

  const matchedInviteHit = inviteSearch.data?.data?.items[0];
  const matchedInviteCandidate: typeChatMemberCandidate | null =
    matchedInviteHit === undefined
      ? null
      : {
          userId: matchedInviteHit.userId,
          username: matchedInviteHit.username,
          displayName: matchedInviteHit.displayName,
          avatarUrl: matchedInviteHit.avatarUrl ?? null,
        };

  const inviteRowState =
    matchedInviteCandidate === null
      ? null
      : resolveInviteRowState({
          candidateUserId: matchedInviteCandidate.userId,
          existingMemberUserIds,
          sentUserIds: sentInviteUserIds,
        });

  const handleInvite = async (
    candidate: typeChatMemberCandidate,
  ): Promise<void> => {
    const result = await inviteMembers.mutateAsync({
      chatId,
      userIds: [candidate.userId],
    });
    if (result.error) {
      showApiError(result);
      return;
    }
    setSentInviteUserIds((prev) => new Set(prev).add(candidate.userId));
  };

  const overflowItems =
    overflowTarget === null
      ? []
      : buildChatMemberOverflowMenu({
          viewerUserId,
          viewerRole: myRole,
          targetUserId: overflowTarget.userId,
          targetRole: overflowTarget.role,
          targetTimedOutUntilIso: overflowTarget.timedOutUntilIso,
        });

  /**
   * Removing a saved member is destructive, so it confirms first
   * (`destructive-confirmation.mdc`, ISSUE-786). The target is captured into
   * `target` before the await, because `overflowTarget` can change while the
   * confirm is open and the mutation must not follow it.
   */
  const handleRemoveMember = async (
    target: typeOverflowTarget,
  ): Promise<void> => {
    const isConfirmed = await confirmDestructive({
      title: buildMemberRemoveConfirmTitle(target.displayName),
      body: MEMBER_REMOVE_CONFIRM_BODY,
      confirmLabel: MEMBER_REMOVE_LABEL,
    });
    if (!isConfirmed) return;
    const result = await removeMember.mutateAsync({
      chatId,
      userId: target.userId,
    });
    if (result.error) {
      showApiError(result);
      return;
    }
    void queryClient.invalidateQueries();
    setOverflowTarget(null);
  };

  const handleOverflowSelect = (id: typeChatMemberMenuActionId): void => {
    if (overflowTarget === null) return;
    // A `switch` rather than an `if` chain: `typeChatMemberMenuActionId` is a
    // four-member union and every member has a case below, so by the last
    // case TypeScript has already narrowed `id` to it. An `if (id === ...)`
    // there would compare a value against the only type it could possibly
    // still be, which `typescript/no-unnecessary-condition` correctly
    // flags; a `switch` case does not trip that rule.
    switch (id) {
      case "removeFromChat":
        void handleRemoveMember(overflowTarget);
        return;
      case "makeAdmin":
        void promoteAdmin
          .mutateAsync({ chatId, userId: overflowTarget.userId })
          .then((result) => {
            if (result.error) {
              showApiError(result);
              return;
            }
            void queryClient.invalidateQueries();
            setOverflowTarget(null);
          });
        return;
      case "timeout":
        setTimeoutTarget(overflowTarget);
        setOverflowTarget(null);
        return;
      case "endTimeout":
        void timeoutClear
          .mutateAsync({ chatId, userId: overflowTarget.userId })
          .then((result) => {
            if (showApiError(result)) return;
            setOverflowTarget(null);
          })
          .catch(() => {
            showOfflineError();
          });
        return;
      case "block":
        // `useBlockFan` raises the shared destructive confirm and the shared
        // error modal itself, so this branch neither confirms nor calls
        // `showApiError` around it, and a block never changes the member
        // list, so there is no invalidation to run after it.
        void blockFan({
          accountId: overflowTarget.userId,
          displayName: overflowTarget.displayName,
        }).then(() => {
          setOverflowTarget(null);
        });
        return;
      case "report":
        // Closes the overflow dialog and opens the report sheet in its
        // place; the two are never open at the same time.
        setReportTarget(overflowTarget);
        setOverflowTarget(null);
        return;
      default: {
        // Exhaustiveness, and the reason this dialog needs no runtime
        // not-ready set. Every id the union has is handled above, so `id` is
        // `never` here and this branch is unreachable today. Add a fifth id to
        // `typeChatMemberMenuActionId` and this assignment stops compiling,
        // which is the signal to wire it or to gate it deliberately. Without
        // it a new id would fall out of the `switch` and silently do nothing.
        const unhandled: never = id;
        return unhandled;
      }
    }
  };

  const handleRenameSave = async (): Promise<void> => {
    const trimmed = renameValue.trim();
    if (trimmed === "") return;
    const result = await updateChat.mutateAsync({ chatId, title: trimmed });
    if (result.error) {
      showApiError(result);
      return;
    }
    void queryClient.invalidateQueries();
    setIsRenaming(false);
  };

  const handleDelete = async (): Promise<void> => {
    const isConfirmed = await confirmDestructive({
      title: CHAT_DELETE_CONFIRM_TITLE,
      body: CHAT_DELETE_CONFIRM_BODY,
      confirmLabel: CHAT_DELETE_LABEL,
    });
    if (!isConfirmed) return;
    const result = await deleteChat.mutateAsync({ chatId });
    if (result.error) {
      showApiError(result);
      return;
    }
    void queryClient.invalidateQueries();
    onOpenChange(false);
    onLeftOrDeleted();
  };

  const handleLeave = async (): Promise<void> => {
    if (isSoleAdmin({ viewerUserId, members })) {
      setShowTransferPrompt(true);
      return;
    }
    const isConfirmed = await confirmDestructive({
      title: CHAT_LEAVE_CONFIRM_TITLE,
      body: CHAT_LEAVE_CONFIRM_BODY,
      confirmLabel: CHAT_LEAVE_LABEL,
    });
    if (!isConfirmed) return;
    const result = await leaveChat.mutateAsync({ chatId });
    if (result.error) {
      if (result.error.code === "LAST_ADMIN_CANNOT_LEAVE") {
        setShowTransferPrompt(true);
        return;
      }
      showApiError(result);
      return;
    }
    void queryClient.invalidateQueries();
    onOpenChange(false);
    onLeftOrDeleted();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setOverflowTarget(null);
          setReportTarget(null);
          setIsInviting(false);
          setInviteUsernameQuery("");
          setSentInviteUserIds(new Set());
          setIsRenaming(false);
          setShowTransferPrompt(false);
        }
        onOpenChange(next);
      }}
      title={CHAT_MEMBERS_TITLE}
      autoFocus={false}
    >
      <div className="flex flex-col gap-4">
        {chatMembers.isLoading ? (
          <div
            className="flex flex-col gap-2"
            data-testid="chat-members-loading"
          >
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : null}
        {!chatMembers.isLoading && members.length === 0 ? (
          <p
            className="text-body text-ink-secondary"
            data-testid="chat-members-empty"
          >
            Nobody has joined yet.
          </p>
        ) : null}
        <ul className="flex flex-col gap-2">
          {members.map((member) => {
            const menuItems = buildChatMemberOverflowMenu({
              viewerUserId,
              viewerRole: myRole,
              targetUserId: member.userId,
              targetRole: member.role,
              targetTimedOutUntilIso: member.timedOutUntilIso ?? null,
            });
            return (
              <li
                key={member.userId}
                className="flex items-center justify-between gap-2"
              >
                <div className="flex items-center gap-2">
                  {viewerUserId !== "" && member.userId !== viewerUserId ? (
                    <Link
                      to="/fan"
                      search={{ accountId: member.userId }}
                      data-testid={`chat-member-profile-${member.userId}`}
                      aria-label={openFanProfileLabel(member.displayName)}
                      className="flex min-h-touch-target items-center text-body text-ink underline-offset-2 hover:underline"
                    >
                      {member.displayName}
                    </Link>
                  ) : (
                    <span className="text-body text-ink">
                      {member.displayName}
                    </span>
                  )}
                  {isChatAdminRole(member.role) ? (
                    <span className="text-caption text-ink-secondary">
                      Admin
                    </span>
                  ) : null}
                </div>
                {menuItems.length > 0 ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    data-testid={`chat-member-more-${member.userId}`}
                    onClick={() => {
                      setOverflowTarget({
                        userId: member.userId,
                        displayName: member.displayName,
                        role: member.role,
                        timedOutUntilIso: member.timedOutUntilIso ?? null,
                      });
                    }}
                  >
                    More
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>

        {isViewerAdmin ? (
          <div className="flex flex-col gap-2">
            {isInviting ? (
              <div
                className="flex flex-col gap-2"
                data-testid="chat-invite-people-panel"
              >
                <Input
                  label={NEW_CHAT_ADD_PEOPLE_SEARCH_LABEL}
                  placeholder="username"
                  data-testid="chat-invite-search"
                  value={inviteUsernameQuery}
                  onChange={(event) => {
                    setInviteUsernameQuery(event.target.value);
                  }}
                />
                {invitePhase === "idle" ? (
                  <p
                    className="text-body text-ink-secondary"
                    data-testid="chat-invite-search-note"
                  >
                    {NEW_CHAT_ADD_PEOPLE_START_MESSAGE}
                  </p>
                ) : null}
                {invitePhase === "searching" ? (
                  <div data-testid="chat-invite-searching">
                    <Skeleton
                      shape="control"
                      label={NEW_CHAT_ADD_PEOPLE_SEARCHING_LABEL}
                    />
                  </div>
                ) : null}
                {invitePhase === "notFound" ? (
                  <p
                    className="text-body text-ink-secondary"
                    data-testid="chat-invite-search-empty"
                  >
                    {NEW_CHAT_ADD_PEOPLE_NOT_FOUND_MESSAGE}
                  </p>
                ) : null}
                {invitePhase === "found" && matchedInviteCandidate !== null ? (
                  <div
                    className="flex min-w-0 items-center gap-3"
                    data-testid={`chat-invite-result-${matchedInviteCandidate.userId}`}
                  >
                    {matchedInviteCandidate.avatarUrl !== null ? (
                      <img
                        src={matchedInviteCandidate.avatarUrl}
                        alt=""
                        className="h-icon-lg w-icon-lg"
                      />
                    ) : null}
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="text-body-strong text-ink break-all">
                        {matchedInviteCandidate.displayName}
                      </span>
                      <span className="text-caption text-ink-secondary break-all">
                        @{matchedInviteCandidate.username}
                      </span>
                    </div>
                    {inviteRowState === "invite" ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        className="ml-auto"
                        loading={inviteMembers.isPending}
                        data-testid={`chat-invite-action-${matchedInviteCandidate.userId}`}
                        onClick={() => {
                          void handleInvite(matchedInviteCandidate);
                        }}
                      >
                        {CHAT_INVITE_SEARCH_ACTION_LABEL}
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled
                        className="ml-auto"
                        data-testid={`chat-invite-action-${matchedInviteCandidate.userId}`}
                      >
                        {inviteRowState === "alreadyMember"
                          ? CHAT_INVITE_ALREADY_MEMBER_LABEL
                          : CHAT_INVITE_SEARCH_SENT_LABEL}
                      </Button>
                    )}
                  </div>
                ) : null}
              </div>
            ) : (
              <Button
                variant="secondary"
                onClick={() => {
                  setIsInviting(true);
                }}
              >
                {CHAT_INVITE_PEOPLE_LABEL}
              </Button>
            )}

            {isRenaming ? (
              <div className="flex flex-col gap-2">
                <Input
                  label={NEW_CHAT_NAME_LABEL}
                  value={renameValue}
                  onChange={(event) => {
                    setRenameValue(event.target.value);
                  }}
                />
                <Button
                  variant="primary"
                  loading={updateChat.isPending}
                  onClick={() => {
                    void handleRenameSave();
                  }}
                >
                  Save
                </Button>
              </div>
            ) : (
              <Button
                variant="secondary"
                onClick={() => {
                  setRenameValue(chatTitle);
                  setIsRenaming(true);
                }}
              >
                {CHAT_RENAME_LABEL}
              </Button>
            )}

            <ChatPhotoPickerButton
              label={CHAT_CHANGE_PHOTO_LABEL}
              isUploading={photoState.kind === "uploading"}
              uploadingLabel={PHOTO_UPLOADING_LABEL}
              onSelectPhoto={uploadPhoto}
              testId="chat-members-photo"
              size="md"
            />

            {photoState.kind === "failed" ? (
              <div data-testid="chat-members-photo-failed">
                <InlineBanner
                  variant="warning"
                  message={photoState.message ?? PHOTO_UPLOAD_FAILED_MESSAGE}
                />
              </div>
            ) : null}

            <Button
              variant="destructive"
              onClick={() => {
                void handleDelete();
              }}
            >
              {CHAT_DELETE_LABEL}
            </Button>
          </div>
        ) : null}

        <Button
          variant="destructive"
          onClick={() => {
            void handleLeave();
          }}
        >
          {CHAT_LEAVE_LABEL}
        </Button>
        {showTransferPrompt ? (
          <div
            className="flex flex-col gap-1"
            data-testid="sole-admin-transfer-prompt"
          >
            <p className="text-body-strong text-ink">
              {isViewerOnlyMember
                ? ONLY_MEMBER_LEAVE_TITLE
                : SOLE_ADMIN_TRANSFER_TITLE}
            </p>
            <p className="text-body text-ink-secondary">
              {isViewerOnlyMember
                ? ONLY_MEMBER_LEAVE_BODY
                : SOLE_ADMIN_TRANSFER_BODY}
            </p>
          </div>
        ) : null}
      </div>

      <Dialog
        open={overflowTarget !== null}
        onOpenChange={(next) => {
          if (!next) {
            setOverflowTarget(null);
          }
        }}
        title={CHAT_MEMBERS_TITLE}
        description={overflowTarget?.displayName ?? ""}
        autoFocus={false}
      >
        <div className="flex flex-col gap-2">
          {overflowItems.map((item) => (
            <Button
              key={item.id}
              fullWidth
              size="md"
              variant={item.destructive ? "destructive" : "secondary"}
              data-testid={`chat-member-action-${item.id}`}
              onClick={() => {
                handleOverflowSelect(item.id);
              }}
            >
              {item.label}
            </Button>
          ))}
        </div>
      </Dialog>

      <MemberTimeoutDialog
        chatId={chatId}
        target={timeoutTarget}
        onClose={() => {
          setTimeoutTarget(null);
        }}
      />

      <ReportAccountDialog
        open={reportTarget !== null}
        onClose={() => {
          setReportTarget(null);
        }}
        reportedAccountId={reportTarget?.userId ?? ""}
        displayName={reportTarget?.displayName ?? ""}
        chatId={chatId}
      />
    </Dialog>
  );
}
